import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import { Autocomplete } from '../autocomplete';
import { TextField } from '../textfield';
import { usePaletteColor } from '../theme';
import { Tooltip } from '../tooltip';
import { withVersion } from '../utils';
import { FacilityInput } from './components/facility-input';
import { FacilityMenuItem } from './components/facility-menu-item';
import { FacilityPopperHeader } from './components/facility-popper';
import { FacilityValue } from './components/facility-value';
import { getFacilityId } from './utils';
export * from './badge';
export * from './bar';
export * from './components/facility-menu-item';
export * from './whitelisting';
const MENU_INPLACE = {
    props: {
        open: true,
        disablePortal: true,
        disabled: true
    },
    paper: {
        sx: {
            boxShadow: 'none'
        }
    },
    popper: {
        sx: {
            position: 'relative !important',
            transform: 'none !important'
        },
        style: {
            width: '100%',
            height: '100%'
        }
    }
};
const DEFAULT_FILTER = { search: '', tags: [] };
export const FACILITY_PICKER_PERMISSION_WIDTH = 220;
export function filterFacilities(options, filter) {
    return options
        .filter((option) => {
        if (filter.tags.length === 0) {
            return true;
        }
        return filter.tags.every((tag) => {
            return option.tags?.some((t) => t.name === tag.name);
        });
    })
        .filter(({ name, facilityName, prefix }) => {
        if (filter.search === '') {
            return true;
        }
        const searchInput = filter.search.toLowerCase();
        const nameMatch = (name ?? facilityName ?? '')
            .toLowerCase()
            .includes(searchInput);
        const prefixMatch = prefix && prefix.toLowerCase().includes(searchInput);
        return nameMatch || prefixMatch;
    });
}
function FacilityPickerImpl(props) {
    const autocompleteInputRef = React.useRef(null);
    const [autocompleteOpen, setAutocompleteOpen] = React.useState(false);
    const [filter, setFilter] = React.useState(DEFAULT_FILTER);
    const bgHover = usePaletteColor('components.facility.background.hover');
    const bgFocus = usePaletteColor('components.facility.background.focus');
    const bgDefault = usePaletteColor('components.facility.background.enabled');
    const bgcolor = (() => {
        if (autocompleteOpen) {
            return bgFocus;
        }
        return props.topNav ? 'transparent' : bgDefault;
    })();
    const getName = (option) => {
        return option.name ?? option.facilityName ?? '';
    };
    const renderMenuInplaceSettings = props.renderMenuInplace
        ? MENU_INPLACE
        : null;
    const picker = (_jsx(Autocomplete, { ...renderMenuInplaceSettings?.props, multiple: props.multiple, disableClearable: true, disableSearch: true, disableCloseOnSelect: props.multiple, fullWidth: false, value: props.value, noOptionsText: props.noOptionsText, onOpen: () => setAutocompleteOpen(true), onClose: () => {
            setAutocompleteOpen(false);
            setFilter(DEFAULT_FILTER);
        }, filterOptions: (options) => filterFacilities(options, filter), onChange: (value, e, reason) => {
            if ((Array.isArray(value) && value.length > 0) ||
                !Array.isArray(value) ||
                props.allowNoneSelected) {
                props.onChange?.(value, e, reason);
            }
        }, menuTheme: props.menuTheme, slotProps: {
            listbox: { sx: { padding: 0, overflowX: 'hidden' } },
            paper: renderMenuInplaceSettings?.paper,
            popper: renderMenuInplaceSettings?.popper || {
                style: { width: 666, maxWidth: '95%' },
                placement: props.placement ?? 'bottom-end'
            },
            popupIndicator: {
                color: 'components.facility.contrast.arrow'
            }
        }, sx: {
            height: renderMenuInplaceSettings ? 0 : 'auto',
            borderRadius: 1,
            maxWidth: 'max-content',
            bgcolor,
            ':hover': {
                bgcolor: autocompleteOpen ? undefined : bgHover
            },
            '[data-role="counter"]': {
                display: 'none !important'
            },
            '.MuiOutlinedInput-root .MuiInputBase-input.MuiAutocomplete-input': {
                minWidth: 0,
                padding: 0
            },
            '&.MuiAutocomplete-root .MuiFormControl-root .MuiInputBase-root': renderMenuInplaceSettings
                ? {
                    opacity: 0,
                    pointerEvents: 'none',
                    minHeight: 0,
                    height: 1,
                    overflow: 'hidden'
                }
                : {
                    paddingTop: 0.75,
                    paddingBottom: 0.75,
                    paddingLeft: 1,
                    height: 36,
                    cursor: 'pointer'
                },
            fieldset: {
                border: 'none'
            }
        }, options: props.options, getOptionLabel: getName, renderTags: () => 
        // FIXME: use renderInput to handle the whole logic, and remove deprecated renderTags
        props.multiple &&
            Array.isArray(props.value) &&
            props.value.length ? (_jsx(FacilityValue, { topNav: props.topNav, reduced: props.reduced, children: props.multipleSelection?.(props.value) })) : null, renderInput: (params) => {
            if (props.multiple &&
                Array.isArray(props.value) &&
                props.value.length) {
                return (_jsx(TextField, { ...params, inputRef: autocompleteInputRef, slotProps: {
                        input: params.InputProps,
                        inputLabel: params.InputLabelProps,
                        htmlInput: params.inputProps
                    } }));
            }
            return (_jsx(FacilityInput, { topNav: props.topNav, reduced: props.reduced, emptySelectionLabel: props.emptySelectionLabel, inputRef: autocompleteInputRef, ...params }));
        }, renderOption: props.renderOption ??
            ((liProps, option, state) => {
                return (_jsx(FacilityMenuItem, { liProps: liProps, option: option, selected: state.selected, multiple: props.multiple, accountNumber: props.accountNumber }, liProps.key));
            }), optionHeight: 69, popperHeader: props.popperHeader ?? FacilityPopperHeader, popperHeaderProps: {
            tags: props.tags,
            filterTags: filter.tags,
            search: filter.search,
            searchPlaceholder: props.searchPlaceholderText,
            autocompleteInputRef,
            permissionConfig: props.permissionConfig,
            selectConfig: Array.isArray(props.value) &&
                props.multiple &&
                props.multipleSelectionFilter &&
                !props.permissionConfig
                ? {
                    selected: props.value.map(getFacilityId),
                    total: filterFacilities(props.options, filter).map(getFacilityId),
                    selectAll: props.multipleSelectionFilter.selectAll,
                    selectNone: props.multipleSelectionFilter.selectCurrent,
                    onChange: (checked, event) => {
                        props.multipleSelectionFilter?.onSelectAll?.(checked);
                        const newValue = (checked
                            ? filterFacilities(props.options, filter)
                            : props.multipleSelectionFilter?.onSelectCurrent());
                        props.onChange?.(newValue, event, 'selectOption');
                    }
                }
                : undefined,
            onFilter: (search, tags) => {
                setFilter({ search, tags });
                props.onFilter?.({ search, tags });
            }
        } }));
    if (props.reduced) {
        return (_jsx(Tooltip, { disabled: autocompleteOpen, title: props.multiple
                ? props.multipleSelection?.(props.value)
                : getName(props.value), children: _jsx("div", { children: picker }) }));
    }
    return picker;
}
export const FacilityPicker = withVersion(FacilityPickerImpl, '1.0.0');
